import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, Users, Building2 } from 'lucide-react';
import { aboutInfo, images } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.talent}
            alt="Our Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/80" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
              About TekClap
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed">
              {aboutInfo.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Key Facts Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Global Reach */}
            <div className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100 card-hover">
              <div className="icon-container mb-6">
                <Globe className="w-6 h-6 text-[#F58B23]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-3">
                {aboutInfo.globalReach.title}
              </h3>
              <p className="text-[#6B7280] text-sm mb-4">
                {aboutInfo.globalReach.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {aboutInfo.globalReach.regions.map((region, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-[#FFF4E6] text-[#F58B23] text-xs font-medium rounded-full"
                  >
                    {region}
                  </span>
                ))}
              </div>
            </div>

            {/* Team Size */}
            <div className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100 card-hover">
              <div className="icon-container mb-6">
                <Users className="w-6 h-6 text-[#F58B23]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-3">
                Our Team
              </h3>
              <div className="text-4xl font-serif font-bold text-[#F58B23] mb-2">
                {aboutInfo.teamSize.value}
              </div>
              <div className="text-[#6B7280] text-sm">
                {aboutInfo.teamSize.description}
              </div>
            </div>

            {/* Growth Story */}
            <div className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100 card-hover">
              <div className="icon-container mb-6">
                <Building2 className="w-6 h-6 text-[#F58B23]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-3">
                Our Story
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {aboutInfo.growthStory}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Image */}
      <section className="py-12 bg-[#FAFAF8]">
        <div className="container-custom">
          <div className="rounded-2xl overflow-hidden">
            <img
              src={images.hero}
              alt="TekClap Innovation"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-4">
              Privilege of Serving Industry Leaders
            </h2>
            <p className="text-[#6B7280]">
              We're proud to partner with forward-thinking organizations.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {aboutInfo.clients.map((client, index) => (
              <div
                key={index}
                className="flex items-center justify-center p-6 bg-white rounded-xl border border-gray-100 h-28 card-hover"
              >
                {client.logo ? (
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="h-10 w-auto max-w-[140px] object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                ) : (
                  <span className="text-[#6B7280] font-semibold text-sm text-center">
                    {client.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Join Our Growing Community
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Whether you're looking to transform your business or join our team, 
              we'd love to hear from you.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-10"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
